import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import type { ProfitPlus2k8SalesPersonIntegration } from '../models/integrations/profitPlus2k8';
import {
  createClientServer,
  deleteClientServer,
  getClientServer,
  listClientsServer,
  updateClientServer,
} from './clientsServer';
import { getSalesPeopleServer, listSalesPeopleForSelect } from './salesPeopleServer';
import {
  createApiListCursor,
  getApiListOffset,
} from '../utils/apiListCursor';

export interface Client {
  id: string;
  code: string;
  rif: string;
  businessName?: string;
  legalName?: string;
  email?: string;
  mobile?: string;
  phone?: string;
  officePhone?: string;
  address?: string;
  city?: string;
  municipality?: string;
  zone?: string;
  referencePoint?: string;
  fiscalAddress?: string;
  shippingAddress?: string;
  alternativeShippingAddress?: string;
  responsible?: string;
  fax?: string;
  description?: string;
  website?: string;
  creatorSalesPerson?: string;

  // MVP: Essential financial fields
  creditLimit?: number;
  creditTerms?: number;

  // MVP: Basic business classification
  customerType?: 'wholesale' | 'retail' | 'distributor' | 'reseller';

  // MVP: Basic sales management
  assignedSalesRep?: string;
  salesZoneId?: string;
  salesZoneName?: string;

  // System fields
  active?: boolean;
  activo?: boolean; // Legacy field for backward compatibility
  approvalState?: 'approved' | 'pending' | 'rejected';
  keywords?: string[];
  palabrasClave?: string[]; // Legacy field for backward compatibility
  created?: Date;
  creado?: any; // Legacy field for backward compatibility
  updated?: Date;

  // Legal representatives
  legalRepresentative1?: {
    fullName: string;
    email: string;
    mobile: string;
    identityNumber: string;
  };
  legalRepresentatives?: Array<{
    fullName: string;
    email: string;
    mobile: string;
    identityNumber: string;
  }>;

  // Legacy fields for backward compatibility
  creatorVendor?: string;
  imageUrl?: string;
  storagePath?: string;
}

export interface ClientQueryResult {
  clients: Client[];
  hasMore: boolean;
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
}

export interface SalesPerson {
  id: string;
  name: string;
  /** Optional: some vendedores only have código / nombre. */
  email?: string;
  code?: string;
  created: any;
  profitPlus2k8?: ProfitPlus2k8SalesPersonIntegration;
}

function pageClientResults(
  clients: Client[],
  offset: number,
  limitCount: number,
  hasMore: boolean
): ClientQueryResult {
  const nextOffset = offset + limitCount;
  return {
    clients,
    hasMore,
    lastDoc: hasMore
      ? (createApiListCursor(nextOffset) as unknown as QueryDocumentSnapshot<DocumentData>)
      : null,
  };
}

/**
 * Load clients one-time (non-realtime) with optional search and limit
 */
export const loadClients = async (
  organizationId: string,
  search: string = '',
  limitCount: number = 10
): Promise<ClientQueryResult> => {
  const result = await listClientsServer(organizationId, {
    q: search.trim() || undefined,
    limit: limitCount,
    offset: 0,
  });
  return pageClientResults(result.clients, 0, limitCount, result.hasMore);
};

/**
 * Get a single client by id.
 *
 * NOTE: This is used by order/sale editing flows where we need the full client
 * details (RIF, email, addresses, etc).
 */
export const getClientById = async (
  organizationId: string,
  clientId: string
): Promise<Client | null> => {
  if (!clientId) return null;

  try {
    return await getClientServer(organizationId, clientId);
  } catch {
    return null;
  }
};

/** Single client fetch (e.g. polling Profit sync status after queueing export). */
export async function fetchClientById(
  organizationId: string,
  clientId: string
): Promise<Client | null> {
  return getClientById(organizationId, clientId);
}

/**
 * Load more clients for pagination
 */
export const loadMoreClients = async (
  organizationId: string,
  search: string = '',
  lastDocument: QueryDocumentSnapshot<DocumentData> | null,
  limitCount: number = 10
): Promise<ClientQueryResult> => {
  const offset = getApiListOffset(lastDocument, 0);
  const result = await listClientsServer(organizationId, {
    q: search.trim() || undefined,
    limit: limitCount,
    offset,
  });
  return pageClientResults(result.clients, offset, limitCount, result.hasMore);
};

/**
 * Search for sales people
 */
export const searchSalesPeople = async (
  organizationId: string,
  search?: string
): Promise<SalesPerson[]> => {
  const people = await listSalesPeopleForSelect(organizationId, {
    search,
  });
  return people.map(sp => ({
    id: sp.id,
    name: sp.name,
    email: sp.email,
    code: sp.code,
    created: sp.created,
    profitPlus2k8: sp.profitPlus2k8,
  }));
};

/**
 * Update client active status
 */
export const updateClientStatus = async (
  organizationId: string,
  clientId: string,
  active: boolean
): Promise<void> => {
  await updateClientServer(organizationId, clientId, { active });
};

/**
 * Delete client and its associated image
 */
export const deleteClient = async (
  organizationId: string,
  client: Client
): Promise<void> => {
  await deleteClientServer(organizationId, client.id);
};

/**
 * Create a new client
 */
export const createClient = async (
  organizationId: string,
  data: Record<string, any>
): Promise<string> => {
  const created = await createClientServer(organizationId, {
    ...data,
    active: data.active !== undefined ? !!data.active : true,
    approvalState: data.approvalState || 'approved',
  });
  return created.id;
};

/**
 * Update client document
 */
export const updateClient = async (
  organizationId: string,
  clientId: string,
  updates: Partial<Client>
): Promise<void> => {
  await updateClientServer(organizationId, clientId, updates as Record<string, unknown>);
};

/**
 * Update client image
 */
export const updateClientImage = async (
  organizationId: string,
  clientId: string,
  imageUrl: string,
  storagePath: string
): Promise<void> => {
  await updateClientServer(organizationId, clientId, { imageUrl, storagePath });
};
